import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Flame } from "lucide-react";
import { AMAZON_URL } from "@/data/brand";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/books", label: "Books & Media" },
  { to: "/programs", label: "Programs" },
  { to: "/robotics", label: "Shop Pluggy" },
  { to: "/blog", label: "Resources" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-white/80 backdrop-blur-xl border-b border-sq-ink/10" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <span className="grid place-items-center w-9 h-9 rounded-xl bg-sq-red text-white shadow-[0_3px_0_#111827] group-hover:-translate-y-0.5 transition-transform">
            <Flame size={20} strokeWidth={2.5} />
          </span>
          <span className="font-display text-2xl tracking-wide leading-none pt-1">
            THE SAFETY QUEST
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]/g, "")}`}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  isActive ? "bg-sq-ink text-white" : "text-sq-ink hover:bg-sq-ink/5"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href={AMAZON_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-buy-cta"
            className="ml-3 px-5 py-2.5 rounded-full bg-sq-yellow text-sq-ink font-bold text-sm btn-chunky"
          >
            Buy the Book
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden grid place-items-center w-10 h-10 rounded-xl bg-sq-ink text-white"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </nav>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-sq-ink/10 px-5 py-4 flex flex-col gap-1">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `px-4 py-3 rounded-xl font-semibold ${isActive ? "bg-sq-ink text-white" : "hover:bg-sq-ink/5"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a href={AMAZON_URL} target="_blank" rel="noopener noreferrer" className="mt-2 text-center px-5 py-3 rounded-xl bg-sq-yellow text-sq-ink font-bold btn-chunky">
            Buy the Book on Amazon
          </a>
        </div>
      )}
    </header>
  );
}
